import React, { useState } from 'react'
import * as S from '../style'
import { ActionProps, TableHeaderProps } from '../types'
import Svg from '@components/svg'

import styled from '@emotion/styled'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Button from '@components/button'
import Skeleton from '@components/skeleton'

interface TableBodyProps {
  columns: TableHeaderProps[]
  data: any[]
  actions?: ActionProps[]
  expandedRowRender?: (rowData: any, index: number) => React.ReactNode
  setId?: (val: string, limit?: number) => void
  setLimit?: (val: number) => void
  onSave?: (data: any) => void
  hasSaveButton?: boolean
  onRowClick?: (data: any) => void
  loading?: boolean
}

const Body: React.FC<TableBodyProps> = ({
  columns,
  data,
  actions = [],
  expandedRowRender,
  setId,
  setLimit,
  onSave,
  hasSaveButton,
  onRowClick,
  loading = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([])
  const toggleRow = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    )
  }

  const isRowExpanded = (index: number) => expandedRows.includes(index)

  return (
    <>
      <S.StyleBodyWrapper>
        {data.length > 0 ? (
          <>
            {data.map((item, index) => (
              <React.Fragment key={index}>
                {loading ? (
                  <S.StyleTableRow>
                    <S.StyleBodyText
                      colSpan={
                        columns.length +
                        (actions.length > 0 ? 1 : 0) +
                        (expandedRowRender ? 1 : 0)
                      }
                    >
                      <Skeleton width="100%" height={60} radius={5} />
                    </S.StyleBodyText>
                  </S.StyleTableRow>
                ) : (
                  <S.StyleTableRow onClick={() => onRowClick?.(item)}>
                    {expandedRowRender && (
                      <S.StyleBodyText
                        onClick={() => {
                          toggleRow(index)
                          const isExpanding = !isRowExpanded(index)
                          if (isExpanding || setId || setLimit) {
                            setId(item.id, item.numberOfPlayers)
                            setLimit(item.numberOfPlayers)
                          }
                        }}
                      >
                        {isRowExpanded(index) ? (
                          <ChevronUp width={20} />
                        ) : (
                          <ChevronDown width={20} />
                        )}
                      </S.StyleBodyText>
                    )}

                    {columns.map((column, colIndex) => (
                      <S.StyleBodyText key={colIndex}>
                        {column.render
                          ? column.render(item, index + 1)
                          : (item[column.key] ?? '-')}
                      </S.StyleBodyText>
                    ))}

                    {actions.length > 0 && (
                      <S.StyleBodyText>
                        <S.ActionButtonWrapper>
                          {actions.map((action) => (
                            <S.ActionButton
                              key={action.key}
                              onClick={(e) => action.onClick(item, e)}
                            >
                              {typeof action.icon === 'string' ? (
                                <Svg src={action.icon} />
                              ) : (
                                action.icon
                              )}
                            </S.ActionButton>
                          ))}
                        </S.ActionButtonWrapper>
                      </S.StyleBodyText>
                    )}
                  </S.StyleTableRow>
                )}

                {isRowExpanded(index) && expandedRowRender && (
                  <StyleExpandedRow>
                    <StyleExpandedCell
                      colSpan={
                        columns.length +
                        (actions.length > 0 ? 1 : 0) +
                        (expandedRowRender ? 1 : 0)
                      }
                    >
                      <>
                        {expandedRowRender(item, index)}
                        {item.hasSaveButton && (
                          <ButtonWrapper>
                            <Button title="Save Details" onClick={onSave} />
                          </ButtonWrapper>
                        )}
                      </>
                    </StyleExpandedCell>
                  </StyleExpandedRow>
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          <S.StyleTableRow>
            <S.StyleBodyText
              colSpan={
                columns.length +
                (actions.length > 0 ? 1 : 0) +
                (expandedRowRender ? 1 : 0)
              }
            >
              nothing here
            </S.StyleBodyText>
          </S.StyleTableRow>
        )}
      </S.StyleBodyWrapper>
    </>
  )
}

export default Body

export const StyleExpandedRow = styled.tr``

export const StyleExpandedCell = styled.td``
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`
